import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import LinearProgress from '@mui/material/LinearProgress';
import MicIcon from '@mui/icons-material/Mic';
import { FaWaveSquare } from 'react-icons/fa';

const AudioRecorder = ({ onAudioRecorded }) => {
  const { t } = useTranslation(['listDataGeneralSettings']);
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const progressIntervalRef = useRef(null);

  const MAX_SECONDS = 60;

  const getSupportedMimeType = () => {
    if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
    if (MediaRecorder.isTypeSupported('audio/mpeg')) return 'audio/mpeg';
    if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
    return '';
  };

  const startRecording = async () => {
    try {
      const mimeType = getSupportedMimeType();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        clearInterval(progressIntervalRef.current);
        setProgress(0);

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const extension = mimeType.includes('mpeg') ? 'mp3' : mimeType.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([audioBlob], `audio-${Date.now()}.${extension}`, { type: mimeType });

        const url = URL.createObjectURL(file);
        setAudioUrl(url);

        if (onAudioRecorded) {
          onAudioRecorded(file);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Progreso
      let seconds = 0;
      progressIntervalRef.current = setInterval(() => {
        seconds++;
        setProgress((seconds / MAX_SECONDS) * 100);
        if (seconds >= MAX_SECONDS) {
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      setError(t('Text_AUDIO_Error') || 'Error al iniciar la grabación');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();

      // ✅ Libera el micrófono deteniendo los tracks
      const tracks = mediaRecorderRef.current.stream.getTracks();
      tracks.forEach((track) => track.stop());

      setIsRecording(false);
    }
  };
 
  const handleDeleteAudio = () => {
    setAudioUrl(null);
    if (onAudioRecorded) onAudioRecorded(null);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant={isRecording ? 'contained' : 'outlined'}
          onClick={isRecording ? stopRecording : startRecording}
          startIcon={
            isRecording ? (
              <Box
                component="span"
                sx={{
                  animation: 'waveMove 0.6s ease-in-out infinite',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FaWaveSquare size={20} />
              </Box>
            ) : (
              <MicIcon />
            )
          }
          sx={{
            borderRadius: '10px',
            borderColor: isRecording ? 'transparent' : '#067330',
            backgroundColor: isRecording ? '#C62828' : '#ffffff',
            color: isRecording ? '#ffffff' : '#067330',
            fontWeight: 'normal',
            animation: isRecording ? 'blinker 1s linear infinite' : 'none',
            '&:hover': {
              backgroundColor: isRecording ? '#b71c1c' : '#067330',
              color: '#ffffff',
              borderColor: '#067330',
            },
            '@keyframes blinker': {
              '50%': { opacity: 0.4 }
            },
            '@keyframes waveMove': {
              '0%, 100%': { transform: 'scaleY(1)' },
              '50%': { transform: 'scaleY(1.5)' }
            }
          }}
        >
          {isRecording ? t('Text_AUDIO_Stop') : t('Text_AUDIO_Save')}
        </Button>

        {isRecording && (
          <Box sx={{ flexGrow: 1, minWidth: 150 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
      </Box>

      {audioUrl && (
        <Box mt={2} display="flex" alignItems="center" gap={2}>
          <audio controls src={audioUrl} style={{ width: 300 }} />
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteAudio}
          >
            {t('Text_AUDIO_Delete')}
          </Button>
        </Box>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        message={error}
      />
    </Box>
  );
};

export default AudioRecorder;